"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, Sparkles, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: 1,
  title: "Pilgrimage Surges & Window Compression",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Handle high-velocity pilgrimage influxes around the Meenakshi Amman Temple and festival periods. We establish dynamic minimum length-of-stay rules and pace-based yield pricing to capture maximum rate during high-demand windows.",
  metricLabel: "Strategy focus",
  metricValue: "Pace-based pacing & minimum stay restrictions"
}, {
  id: 2,
  title: "Aggressive Comp-Set Undercutting",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Track intraday rate movements across MakeMyTrip, Agoda, and Booking.com for Madurai hotels. Automated competitive alerts and rule-based fencing protect your RevPAR while securing advance leisure and commercial bookings.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA monitoring & rule-based rate fencing"
}, {
  id: 3,
  title: "The OTA Commission & Parity Trap",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Stop unauthorized B2B wholesaler leakage and reclaim revenue lost to high OTA distribution costs. We enforce strict rate parity across metasearch engines and drive direct bookings from returning pilgrims and corporate travelers.",
  metricLabel: "Strategy focus",
  metricValue: "Wholesaler rate fencing & direct channel boost"
}];
const seasonalitySeasons = [{
  period: "Oct – Mar (Peak Season)",
  demand: "Maximum Pilgrimage & Heritage Peak",
  strategy: "Dynamic ADR optimization, strict 2-3 night MLOS on festival weekends, and advance non-refundable rate tiers."
}, {
  period: "Apr – May (Chithirai Festival Surge)",
  demand: "Hyper-Compression & City-Wide Sellouts",
  strategy: "Aggressive inventory rationing, premium room category upselling, and closing discount channels weeks in advance."
}, {
  period: "Jun – Sep (Shoulder & Monsoons)",
  demand: "Corporate, Medical & Regional Transit Flow",
  strategy: "Targeted corporate packages, localized direct-booking perks, and geo-targeted OTA promotion campaigns."
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapemadurai" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Madurai
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Madurai operates as a vital cultural, spiritual, and commercial hub in Tamil Nadu, driven by Meenakshi Amman Temple pilgrimages, heritage circuits, and business transit. Sunil Singh engineers precision revenue strategies designed specifically for city-center hotels, boutique stays, and transit resorts in Madurai.
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Seasonality Block */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.15
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="mt-12 rounded-lg border border-border bg-card p-6 md:p-8 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-border/70">
            <div className="flex items-center gap-3">
              <div className="inline-flex size-10 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                <TrendingUp className="size-5 text-secondary" />
              </div>
              <div>
                <h3 className="text-lg md:text-xl font-bold text-foreground">
                  Madurai Demand Seasonality & Revenue Cadence
                </h3>
                <p className="text-xs md:text-sm text-muted-foreground">
                  Structured demand cycles dictating rate fencing, length-of-stay controls, and inventory allocation.
                </p>
              </div>
            </div>
            <Badge variant="secondary" className="w-fit self-start md:self-auto gap-1">
              <Sparkles className="size-3" />
              Annual Yield Playbook
            </Badge>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6">
            {seasonalitySeasons.map((season, i) => <div key={season.period} data-index={i} className="flex flex-col justify-between rounded-md border border-border/60 bg-muted/30 p-4">
                <div>
                  <span className="text-xs font-mono font-semibold uppercase tracking-wider text-secondary">
                    {season.period}
                  </span>
                  <h4 className="text-sm font-semibold text-foreground mt-1 mb-2">
                    {season.demand}
                  </h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {season.strategy}
                  </p>
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Madurai hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}